import React, { useState, useEffect } from 'react';
import './AddUser.css'; // Reusing AddUser styles
import { api } from '../config/api';

interface User {
  userId: string;
  displayUserId: string;
  name: string;
  email: string;
  phoneNo: string;
  role: string;
  isVerified: boolean;
  isBanned: boolean;
  bannedAt?: string | null;
  dateOfBirth?: string | null;
  createdOn?: string;
  createdBy?: string;
  modifiedOn?: string;
  modifiedBy?: string;
  isDeleted?: boolean;
}

interface EditUserProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  user: User;
}

const EditUser: React.FC<EditUserProps> = ({ isOpen, onClose, onSave, user }) => {
  const [userData, setUserData] = useState<User>({ ...user });
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Add keyboard event handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Enter" && !isLoading && hasChanges && userData.name && userData.email && userData.phoneNo) {
        // Only trigger save if form is valid and has changes
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "SELECT" && target.tagName !== "TEXTAREA") {
          handleSave();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isLoading, hasChanges, userData, onClose]);

  const handleInputChange = (field: keyof User, value: string) => {
    setUserData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    if (field === 'email') {
      setEmailError(null);
    }
    // Clear any previous errors when user makes changes
    setError(null);
  };

  const handleSave = async () => {
    if (!userData.name || !userData.email || !userData.phoneNo) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      // First, check if user exists
      const checkResponse = await api.get(`/users/get-user-by-id/${userData.userId}`);
      const checkData = checkResponse.data;

      if (!checkData.success) {
        alert('User not found');
        setIsLoading(false);
        return;
      }

      // Prepare update data with only allowed fields for UserUpdateDTO
      const updateData = {
        name: userData.name,
        email: userData.email.toLowerCase(),
        phoneNo: userData.phoneNo || null,
        dateOfBirth: userData.dateOfBirth || null,
        role: userData.role,
        modifiedBy: 'SUPERADMIN_USER_ID',
        deleted: false
      };

      const updateResponse = await api.put(`/users/update-user-details/${userData.userId}`, updateData);
      const updateData_result = updateResponse.data;

      if (updateData_result.success) {
        console.log('User updated successfully');
        onSave();
      } else {
        console.error('Failed to update user:', updateData_result.message);
        alert('Failed to update user: ' + updateData_result.message);
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      let errorMessage = 'Failed to update user';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Edit User</h2>
        </div>

        <div className="new-user-form">
          <div className="form-group">
            <label>Name:</label>
            <input
              type="text"
              value={userData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter name"
              required
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              value={userData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email"
              required
              disabled={isLoading}
            />
            {emailError && <div className="error">{emailError}</div>}
          </div>
          <div className="form-group">
            <label>Phone Number (Optional):</label>
            <input
              type="tel"
              value={userData.phoneNo}
              onChange={(e) => handleInputChange('phoneNo', e.target.value)}
              placeholder="Enter phone number"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label>Role:</label>
            <select
              value={userData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              disabled={isLoading}
            >
              <option value="SuperAdmin">Super Admin</option>
              <option value="Operator">Operator</option>
            </select>
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="button-container">
          <button
            className="confirm-button"
            onClick={handleSave}
            disabled={isLoading || !hasChanges || !userData.name || !userData.email}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            className="cancel-button"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUser; 